// GuardWarningModal — Shows triggered guards with severity. Override has 5s delay.
import React, { useState, useEffect } from 'react';
import { useMind } from '../../../context/MindContext';
import { FaShieldAlt, FaExclamationTriangle, FaBan, FaTimes } from 'react-icons/fa';

const SEVERITY_STYLES = {
  warn: {
    icon: <FaExclamationTriangle />,
    bg: 'bg-yellow-50', border: 'border-yellow-200',
    iconColor: 'text-yellow-500', textColor: 'text-yellow-800',
  },
  block: {
    icon: <FaBan />,
    bg: 'bg-red-50', border: 'border-red-200',
    iconColor: 'text-red-500', textColor: 'text-red-800',
  },
  prompt: {
    icon: <FaShieldAlt />,
    bg: 'bg-blue-50', border: 'border-blue-200',
    iconColor: 'text-blue-500', textColor: 'text-blue-800',
  },
};

export default function GuardWarningModal() {
  const { showGuardWarning, guardWarningData, handleGuardOverride, handleGuardCancel } = useMind();
  const [overrideTimer, setOverrideTimer] = useState(5);
  const [canOverride, setCanOverride] = useState(false);

  const guards = guardWarningData?.guards || [];
  const hasBlock = guards.some(g => g.severity === 'block' && !g.overridable);
  const allOverridable = guards.every(g => g.overridable);

  // 5-second delay before override button activates
  useEffect(() => {
    if (!showGuardWarning || hasBlock) return;
    setOverrideTimer(5);
    setCanOverride(false);

    const interval = setInterval(() => {
      setOverrideTimer(prev => {
        if (prev <= 1) {
          setCanOverride(true);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showGuardWarning, hasBlock]);

  if (!showGuardWarning || guards.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[210] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              hasBlock ? 'bg-red-50' : 'bg-yellow-50'
            }`}>
              {hasBlock ? <FaBan className="text-red-600" /> : <FaShieldAlt className="text-yellow-600" />}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                {hasBlock ? 'Order Blocked' : 'Guard Warning'}
              </h2>
              <p className="text-[11px] text-gray-500">
                {guards.length} guard{guards.length > 1 ? 's' : ''} triggered
              </p>
            </div>
          </div>
          <button
            onClick={handleGuardCancel}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6 space-y-3">
          {/* Guard list */}
          {guards.map((guard, i) => {
            const style = SEVERITY_STYLES[guard.severity] || SEVERITY_STYLES.warn;
            return (
              <div key={i} className={`p-3 rounded-lg border ${style.bg} ${style.border}`}>
                <div className="flex items-start gap-2">
                  <span className={`mt-0.5 text-sm ${style.iconColor}`}>{style.icon}</span>
                  <div>
                    <p className={`text-xs font-semibold ${style.textColor}`}>{guard.label}</p>
                    <p className="text-[11px] text-gray-600 mt-0.5">{guard.description}</p>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleGuardCancel}
              className="flex-1 py-2.5 rounded-lg text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Cancel Order
            </button>

            {allOverridable && !hasBlock && (
              <button
                onClick={canOverride ? handleGuardOverride : undefined}
                disabled={!canOverride}
                className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-colors ${
                  canOverride
                    ? 'bg-yellow-500 text-white hover:bg-yellow-600 cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {canOverride ? 'Override & Proceed' : `Wait ${overrideTimer}s...`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
